/**
 * TOSS Web Application - UI Store
 *
 * Zustand store for UI state management:
 * - Sidebar collapsed/expanded state
 * - Theme (light/dark mode)
 * - Mobile menu visibility
 * - Notifications panel visibility
 * - Toast messages queue
 * - Modal states
 *
 * Uses persist middleware to save preferences to localStorage
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useCallback } from 'react';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ConfirmationModal,
  Theme,
  Toast,
  UIState,
  UIStore,
} from './uiStore.types';

// ============================================================================
// Types
// ============================================================================

export type { Theme, ToastType, Toast, ConfirmationModal } from './uiStore.types';

// ============================================================================
// Initial State
// ============================================================================

const initialConfirmationModal: ConfirmationModal = {
  isOpen: false,
  title: '',
  message: '',
  confirmLabel: 'Подтвердить',
  cancelLabel: 'Отмена',
  variant: 'info',
};

const initialState: UIState = {
  // Sidebar - default expanded
  isSidebarCollapsed: false,

  // Calculator panel - default closed
  isCalculatorPanelOpen: false,

  // Command palette - default closed (ephemeral, not persisted)
  isCommandPaletteOpen: false,

  // Mobile - default closed
  isMobileMenuOpen: false,

  // Theme - default system
  theme: 'system',

  // Notifications - default closed
  isNotificationsPanelOpen: false,

  // Toasts - empty
  toasts: [],

  // Confirmation modal - closed
  confirmationModal: initialConfirmationModal,

  // Page loading - not loading
  isPageLoading: false,

  // Page title - empty
  pageTitle: '',

  // Vision v4 Phase 4: landscape blocker enabled by default on mobile
  disableLandscapeOnMobile: true,
};

// ============================================================================
// Utilities
// ============================================================================

/**
 * Generate unique ID for toasts
 */
function generateToastId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Default toast duration in milliseconds
 */
const DEFAULT_TOAST_DURATION = 5000;

// ============================================================================
// Store
// ============================================================================

/**
 * UI store instance with persistence
 *
 * Persisted state:
 * - theme
 * - isSidebarCollapsed
 *
 * Non-persisted (transient) state:
 * - isMobileMenuOpen
 * - isNotificationsPanelOpen
 * - toasts
 * - confirmationModal
 * - isPageLoading
 * - pageTitle
 *
 * Usage:
 * ```tsx
 * const theme = useUIStore((state) => state.theme);
 * const setTheme = useUIStore((state) => state.setTheme);
 * const showSuccess = useUIStore((state) => state.showSuccess);
 *
 * // Show toast
 * showSuccess('Сохранено', 'Данные успешно сохранены');
 * ```
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========== Sidebar Actions ==========

      toggleSidebar: () => {
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed) => {
        set({ isSidebarCollapsed: collapsed });
      },

      // ========== Calculator Panel Actions ==========

      toggleCalculatorPanel: () => {
        set((state) => ({ isCalculatorPanelOpen: !state.isCalculatorPanelOpen }));
      },

      setCalculatorPanelOpen: (open) => {
        set({ isCalculatorPanelOpen: open });
      },

      openCalculatorPanel: () => {
        set({ isCalculatorPanelOpen: true });
      },

      closeCalculatorPanel: () => {
        set({ isCalculatorPanelOpen: false });
      },

      // ========== Command Palette Actions ==========

      openCommandPalette: () => {
        set({ isCommandPaletteOpen: true });
      },

      closeCommandPalette: () => {
        set({ isCommandPaletteOpen: false });
      },

      toggleCommandPalette: () => {
        set((state) => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen }));
      },

      // ========== Mobile Actions ==========

      toggleMobileMenu: () => {
        set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }));
      },

      setMobileMenuOpen: (open) => {
        set({ isMobileMenuOpen: open });
      },

      openMobileMenu: () => {
        set({ isMobileMenuOpen: true });
      },

      closeMobileMenu: () => {
        set({ isMobileMenuOpen: false });
      },

      // ========== Theme Actions ==========

      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        applyTheme(theme);
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme: Theme = currentTheme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        applyTheme(newTheme);
      },

      // ========== Notifications Panel Actions ==========

      toggleNotificationsPanel: () => {
        set((state) => ({
          isNotificationsPanelOpen: !state.isNotificationsPanelOpen,
        }));
      },

      setNotificationsPanelOpen: (open) => {
        set({ isNotificationsPanelOpen: open });
      },

      closeNotificationsPanel: () => {
        set({ isNotificationsPanelOpen: false });
      },

      // ========== Toast Actions ==========

      addToast: (toast) => {
        const id = generateToastId();
        const newToast: Toast = {
          id,
          duration: DEFAULT_TOAST_DURATION,
          dismissible: true,
          ...toast,
        };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        /* Таймер авто-скрытия живёт в `ToastItem`, а НЕ здесь — и это не перестановка ради
           красоты. Здешний `setTimeout` не знал про курсор: `ToastItem` честно приостанавливал
           свой таймер и полосу на наведении, а стор всё равно сносил тост ровно через
           `duration` — прочитать длинное сообщение было нельзя в принципе. Второй эффект был
           тише и хуже: тосты сверх трёх видимых удалялись, так и не показавшись. Теперь
           лишние ждут очереди, а снимает тост тот, кто знает, читают его сейчас или нет. */

        return id;
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearAllToasts: () => {
        set({ toasts: [] });
      },

      showSuccess: (title, message) => {
        return get().addToast({ type: 'success', title, message });
      },

      showError: (title, message) => {
        return get().addToast({
          type: 'error',
          title,
          message,
          duration: 8000, // Errors stay longer
        });
      },

      showWarning: (title, message) => {
        return get().addToast({ type: 'warning', title, message });
      },

      showInfo: (title, message) => {
        return get().addToast({ type: 'info', title, message });
      },

      // ========== Confirmation Modal Actions ==========

      showConfirmation: (options) => {
        set({
          confirmationModal: {
            ...initialConfirmationModal,
            ...options,
            isOpen: true,
          },
        });
      },

      hideConfirmation: () => {
        const { onCancel } = get().confirmationModal;
        onCancel?.();
        set({
          confirmationModal: initialConfirmationModal,
        });
      },

      confirmAction: async () => {
        const { onConfirm } = get().confirmationModal;
        if (onConfirm) {
          await onConfirm();
        }
        set({
          confirmationModal: initialConfirmationModal,
        });
      },

      // ========== Page Loading Actions ==========

      setPageLoading: (loading) => {
        set({ isPageLoading: loading });
      },

      // ========== Page Title Actions ==========

      setPageTitle: (title) => {
        set({ pageTitle: title });
        // Also update document title
        document.title = title ? `${title} | TOSS` : 'TOSS';
      },

      // ========== Landscape Blocker Actions (Vision v4 Phase 4) ==========

      setDisableLandscapeOnMobile: (disabled) => {
        set({ disableLandscapeOnMobile: disabled });
      },

      // ========== Reset Actions ==========

      resetTransientState: () => {
        set({
          isMobileMenuOpen: false,
          isNotificationsPanelOpen: false,
          toasts: [],
          confirmationModal: initialConfirmationModal,
          isPageLoading: false,
          pageTitle: '',
        });
      },
    }),
    {
      name: 'toss-ui-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist preferences, not transient state
      partialize: (state) => ({
        theme: state.theme,
        isSidebarCollapsed: state.isSidebarCollapsed,
        isCalculatorPanelOpen: state.isCalculatorPanelOpen,
        disableLandscapeOnMobile: state.disableLandscapeOnMobile,
      }),
      // Apply theme on rehydration
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

// ============================================================================
// Theme Utilities
// ============================================================================

/**
 * Apply theme to document
 */
function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  // Remove existing theme classes
  root.classList.remove('light', 'dark');

  if (theme === 'system') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.add(prefersDark ? 'dark' : 'light');
  } else {
    root.classList.add(theme);
  }
}

/**
 * Get resolved theme (light or dark) based on current setting
 */
export function getResolvedTheme(): 'light' | 'dark' {
  const theme = useUIStore.getState().theme;

  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return theme;
}

/**
 * Setup system theme change listener
 * Call this on app mount to react to system theme changes
 */
export function setupThemeListener(): () => void {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = (): void => {
    const theme = useUIStore.getState().theme;
    if (theme === 'system') {
      applyTheme('system');
    }
  };

  mediaQuery.addEventListener('change', handler);

  // Return cleanup function
  return () => {
    mediaQuery.removeEventListener('change', handler);
  };
}

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select sidebar collapsed state
 */
export const selectIsSidebarCollapsed = (state: UIStore): boolean =>
  state.isSidebarCollapsed;

/**
 * Select calculator panel open state
 */
export const selectIsCalculatorPanelOpen = (state: UIStore): boolean =>
  state.isCalculatorPanelOpen;

/**
 * Select mobile menu open state
 */
export const selectIsMobileMenuOpen = (state: UIStore): boolean =>
  state.isMobileMenuOpen;

/**
 * Select theme
 */
export const selectTheme = (state: UIStore): Theme => state.theme;

/**
 * Select notifications panel open state
 */
export const selectIsNotificationsPanelOpen = (state: UIStore): boolean =>
  state.isNotificationsPanelOpen;

/**
 * Select toasts
 */
export const selectToasts = (state: UIStore): Toast[] => state.toasts;

/**
 * Select confirmation modal state
 */
export const selectConfirmationModal = (state: UIStore): ConfirmationModal =>
  state.confirmationModal;

/**
 * Select page loading state
 */
export const selectIsPageLoading = (state: UIStore): boolean =>
  state.isPageLoading;

/**
 * Select page title
 */
export const selectPageTitle = (state: UIStore): string => state.pageTitle;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get sidebar state
 */
export function useSidebar(): {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
} {
  return useUIStore(
    useShallow((state) => ({
      isCollapsed: state.isSidebarCollapsed,
      toggle: state.toggleSidebar,
      setCollapsed: state.setSidebarCollapsed,
    }))
  );
}

/**
 * Hook to get calculator panel state
 */
export function useCalculatorPanel(): {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
} {
  return useUIStore(
    useShallow((state) => ({
      isOpen: state.isCalculatorPanelOpen,
      toggle: state.toggleCalculatorPanel,
      open: state.openCalculatorPanel,
      close: state.closeCalculatorPanel,
    }))
  );
}

/**
 * Hook to get command palette (global search) state
 */
export function useCommandPalette(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} {
  return useUIStore(
    useShallow((state) => ({
      isOpen: state.isCommandPaletteOpen,
      open: state.openCommandPalette,
      close: state.closeCommandPalette,
      toggle: state.toggleCommandPalette,
    }))
  );
}

/**
 * Hook to get mobile menu state
 */
export function useMobileMenu(): {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
} {
  return useUIStore(
    useShallow((state) => ({
      isOpen: state.isMobileMenuOpen,
      toggle: state.toggleMobileMenu,
      open: state.openMobileMenu,
      close: state.closeMobileMenu,
    }))
  );
}

/**
 * Hook to get theme state and actions
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
  isDark: boolean;
} {
  const theme = useUIStore(selectTheme);
  const setTheme = useUIStore((state) => state.setTheme);
  const toggle = useUIStore((state) => state.toggleTheme);

  return {
    theme,
    setTheme,
    toggle,
    isDark: getResolvedTheme() === 'dark',
  };
}

/**
 * Hook to get toast actions
 */
export function useToast(): {
  toasts: Toast[];
  add: (toast: Omit<Toast, 'id'>) => string;
  addToast: (toast: Omit<Toast, 'id'>) => string;
  remove: (id: string) => void;
  clear: () => void;
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  showToast: (options: { type: Toast['type']; message: string; title?: string }) => string;
} {
  const store = useUIStore(
    useShallow((state) => ({
      toasts: state.toasts,
      add: state.addToast,
      remove: state.removeToast,
      clear: state.clearAllToasts,
      success: state.showSuccess,
      error: state.showError,
      warning: state.showWarning,
      info: state.showInfo,
    }))
  );

  // Add showToast helper that accepts { type, message, title? }.
  // MUST be memoized: callers put `showToast` in useCallback/useEffect deps, and
  // an unstable reference re-fires those effects every render (e.g. the edit
  // page's option preload looped forever → pickers stuck disabled + error toasts
  // re-spawning so they couldn't be dismissed). The store actions are stable.
  // Depend on the individual store actions (stable Zustand references), NOT the
  // whole `store` object — `store.toasts` changes on every add/remove, which would
  // make showToast unstable again and reintroduce the loop.
  const { success, error, warning, info } = store;
  const showToast = useCallback(
    (options: { type: Toast['type']; message: string; title?: string }): string => {
      const { type, message, title } = options;
      switch (type) {
        case 'success':
          return success(title ?? message, title ? message : undefined);
        case 'error':
          return error(title ?? message, title ? message : undefined);
        case 'warning':
          return warning(title ?? message, title ? message : undefined);
        case 'info':
        default:
          return info(title ?? message, title ? message : undefined);
      }
    },
    [success, error, warning, info]
  );

  return {
    ...store,
    addToast: store.add, // Alias for add
    showToast,
  };
}

/**
 * Hook to manage confirmation modal
 */
export function useConfirmation(): {
  modal: ConfirmationModal;
  show: (options: Omit<ConfirmationModal, 'isOpen'>) => void;
  hide: () => void;
  confirm: () => Promise<void>;
} {
  return useUIStore(
    useShallow((state) => ({
      modal: state.confirmationModal,
      show: state.showConfirmation,
      hide: state.hideConfirmation,
      confirm: state.confirmAction,
    }))
  );
}

/**
 * Hook to get notifications panel state
 */
export function useNotificationsPanel(): {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
} {
  return useUIStore(
    useShallow((state) => ({
      isOpen: state.isNotificationsPanelOpen,
      toggle: state.toggleNotificationsPanel,
      open: () => state.setNotificationsPanelOpen(true),
      close: state.closeNotificationsPanel,
    }))
  );
}

